import React, { useCallback, useEffect, useState, useMemo } from "react";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import DownloadIcon from "@styles/all-images/icons/download.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import ListIcon from "@styles/all-images/icons/list-icon.svg";
import UnarchiveIcon from "@styles/all-images/icons/unarchived.svg";
import MasterModal from "./MasterModal";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { EndpointType } from "@/constants/api";
import { usePermissionState } from "@/providers/PermissionProvider";
import {
  BasePermissions,
  ClientTypesPermissions,
  CommodityPermissions,
  DebitorsPermissions,
  PaymentTermsPermissions,
  ProfessionsPermissions,
} from "@/constants/permission-modules/base-permissions";

export type MasterType =
  | "commodity"
  | "profession"
  | "debtorType"
  | "paymentTerms"
  | "clientType"
  | "bank";

export interface MasterDataTableProps {
  type: MasterType;
  fetchData: (
    type: MasterType,
    params: { limit?: number; page?: number; search?: string },
    isArchiveView: boolean,
  ) => Promise<any>;
}

const MasterDataTable: React.FC<MasterDataTableProps> = ({
  type,
  fetchData,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [columns, setColumns] = useState<any>([]);
  const { isPermitted } = usePermissionState();

  const titleNameObject = useMemo(
    () => ({
      commodity: {
        title: "title",
        display: "Commodity",
      },
      profession: {
        title: "title",
        display: "Profession",
      },
      debtorType: {
        title: "debtor_type",
        display: "Debtor Type",
      },
      paymentTerms: {
        title: "payment_term",
        display: "Payment Terms",
      },
      clientType: {
        title: "client_type",
        display: "Client Type",
      },
      bank: {
        title: "name",
        display: "Bank",
      },
    }),
    [],
  );

  interface TypeConfig {
    addText: string;
    editPath: string;
    addPath: string;
    permissions?: {
      add: BasePermissions;
      edit: BasePermissions;
      archive: BasePermissions;
    };
  }

  const typeConfig: Record<MasterType, TypeConfig> = {
    commodity: {
      addText: "Add Commodity",
      editPath: "/edit-commodity",
      addPath: "/add-commodity",
      permissions: {
        add: CommodityPermissions.CREATE_COMMODITY,
        edit: CommodityPermissions.UPDATE_COMMODITY,
        archive: CommodityPermissions.ARCHIVE_COMMODITY,
      },
    },
    profession: {
      addText: "Add Profession",
      editPath: "/edit-profession",
      addPath: "/add-profession",
      permissions: {
        add: ProfessionsPermissions.CREATE_PROFESSIONS,
        edit: ProfessionsPermissions.UPDATE_PROFESSIONS,
        archive: ProfessionsPermissions.ARCHIVE_PROFESSIONS,
      },
    },
    debtorType: {
      addText: "Add Debtor Type",
      editPath: "/edit-debtor-type",
      addPath: "/add-debtor-type",
      permissions: {
        add: DebitorsPermissions.CREATE_DEBITOR_TYPE,
        edit: DebitorsPermissions.UPDATE_DEBITOR_TYPE,
        archive: DebitorsPermissions.ARCHIVE_DEBITOR_TYPE,
      },
    },
    paymentTerms: {
      addText: "Add Payment Terms",
      editPath: "/edit-payment-terms",
      addPath: "/add-payment-terms",
      permissions: {
        add: PaymentTermsPermissions.CREATE_PAYMENT_TERMS,
        edit: PaymentTermsPermissions.UPDATE_PAYMENT_TERMS,
        archive: PaymentTermsPermissions.ARCHIVE_PAYMENT_TERMS,
      },
    },
    clientType: {
      addText: "Add Client Type",
      editPath: "/edit-client-type",
      addPath: "/add-client-type",
      permissions: {
        add: ClientTypesPermissions.CREATE_CLIENT_TYPES,
        edit: ClientTypesPermissions.UPDATE_CLIENT_TYPES,
        archive: ClientTypesPermissions.ARCHIVE_CLIENT_TYPES,
      },
    },
    bank: {
      addText: "Add Bank",
      editPath: "/edit-bank",
      addPath: "/add-bank",
    },
  };

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      return fetchData(type, params, isArchiveView);
    },
    [type, isArchiveView, fetchData],
  );

  const handleArchived = useCallback(
    async (row: any) => {
      try {
        const { authenticatedCall: archiveMaster } = actionsHandler(
          (type + "Archive") as EndpointType,
        );

        const newArchiveState = !row.is_archive;

        const payload = {
          is_archive: newArchiveState,
        };

        const response = await dispatch(
          archiveMaster({
            payload,
            method: "patch",
            customParams: [row.id + "/"],
          }),
        );

        if (response.ok) {
          toast.success(
            response.data?.message ||
              `${titleNameObject[type].display} ${newArchiveState ? "archived" : "unarchived"} successfully`,
          );
          setForceRefresh(true);
        }
      } catch (error) {
        console.error("Archive error:", error);
      }
    },
    [type, dispatch, titleNameObject],
  );

  const toggleArchiveView = () => {
    setIsArchiveView(!isArchiveView);
  };

  const handleSettingColumns = useCallback(() => {
    setColumns([
      {
        header: titleNameObject[type].display,
        accessor: titleNameObject[type].title,
        sortable: true,
        sortKey: titleNameObject[type].title,
      },
      {
        header: "Updated On",
        accessor: (row: any) => formatDate(row.updated_at),
        sortable: true,
        sortKey: "updated_at",
      },
      ...(isPermitted(
        typeConfig[type].permissions?.archive as BasePermissions,
      ) || isPermitted(typeConfig[type].permissions?.edit as BasePermissions)
        ? [
            {
              header: "Action",
              accessor: (row: any) => (
                <div className="tw-flex tw-items-center">
                  {!isArchiveView ? (
                    <>
                      {isPermitted(
                        typeConfig[type].permissions?.edit as BasePermissions,
                      ) && (
                        <button
                          onClick={() => {
                            setSelectedItem(row);
                            setModalMode("edit");
                            setModalOpen(true);
                          }}
                          title="Edit"
                        >
                          <img
                            src={EditIcon}
                            alt="Edit Icon"
                            className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                          />
                        </button>
                      )}
                      {isPermitted(
                        typeConfig[type].permissions
                          ?.archive as BasePermissions,
                      ) && (
                        <button
                          className="tw-text-blue-500"
                          onClick={() => handleArchived(row)}
                          title="Archive"
                        >
                          <img
                            src={DownloadIcon}
                            alt="Download Icon"
                            className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                          />
                        </button>
                      )}
                    </>
                  ) : (
                    isPermitted(
                      typeConfig[type].permissions?.archive as BasePermissions,
                    ) && (
                      <button
                        className="tw-text-blue-500"
                        onClick={() => handleArchived(row)}
                        title="Unarchive"
                      >
                        <img
                          src={isArchiveView ? UnarchiveIcon : DownloadIcon}
                          alt={
                            isArchiveView ? "Unarchive Icon" : "Archive Icon"
                          }
                          className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                        />
                      </button>
                    )
                  )}
                </div>
              ),
            },
          ]
        : []),
    ]);
  }, [type, isArchiveView, handleArchived]);

  useEffect(() => {
    handleSettingColumns();
    setForceReset(true);
  }, [isArchiveView]);

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={handleFetch}
        addButton={
          (
            typeConfig[type].permissions?.add
              ? isPermitted(
                  typeConfig[type].permissions?.add as BasePermissions,
                )
              : true
          )
            ? {
                text: typeConfig[type].addText,
                icon: PlusIcon,
                onClick: () => {
                  setModalMode("create");
                  setSelectedItem(null);
                  setModalOpen(true);
                },
              }
            : undefined
        }
        archiveButton={{
          text: isArchiveView ? "Back to List" : "Archived",
          icon: isArchiveView ? ListIcon : DownloadIcon,
          onClick: toggleArchiveView,
          isArchiveView,
        }}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        noDataMessage={`No ${titleNameObject[type].display} found`}
        noSearchDataMessage={`No matching ${titleNameObject[type].display} found`}
      />

      {modalOpen && (
        <MasterModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          type={type}
          mode={modalMode}
          titleConfig={titleNameObject[type]}
          initialData={selectedItem}
          onSuccess={() => setForceRefresh(true)}
        />
      )}
    </>
  );
};

export default MasterDataTable;
